import { useMemo } from 'react';
import { useAuth } from './useAuth';
import {
  Permission,
  RoleId,
  ROLE_PERMISSIONS,
  ROLE_NAMES,
  ROUTE_PERMISSIONS,
  getDefaultRedirectPath,
} from '../types/permissions';

export function usePermissions() {
  const { user } = useAuth();

  const roleId = user?.roleId as RoleId | undefined;

  const permissions = useMemo(() => {
    if (!roleId) return [];
    return ROLE_PERMISSIONS[roleId] || [];
  }, [roleId]);

  const hasPermission = (permission: Permission): boolean => {
    return permissions.includes(permission);
  };

  const canAccessRoute = (path: string): boolean => {
    const permission = ROUTE_PERMISSIONS[path];
    if (!permission) return true; // Routes without permission requirement are accessible
    return hasPermission(permission);
  };

  const roleName = useMemo(() => {
    if (user?.superAdmin) return 'Super Admin';
    if (!roleId) return '';
    return ROLE_NAMES[roleId] || `Rol ${roleId}`;
  }, [roleId, user?.superAdmin]);

  const defaultRedirectPath = useMemo(() => {
    if (!roleId) return '/';
    return getDefaultRedirectPath(roleId);
  }, [roleId]);

  // View permissions (routes)
  const canViewDashboard = hasPermission('view:dashboard');
  const canViewRegistro = hasPermission('view:registro');
  const canViewScuba3s = hasPermission('view:scuba3s');
  const canViewHistorialAlertas = hasPermission('view:historial-alertas');
  const canViewAnalisis = hasPermission('view:analisis');
  const canViewReportes = hasPermission('view:reportes');
  const canViewConfiguracion = hasPermission('view:configuracion');
  const canViewQuimicos = hasPermission('view:quimicos');
  const canViewInventario = hasPermission('view:inventario');
  const canViewAdmin = hasPermission('view:admin');

  // Pool permissions
  const canReadPool = hasPermission('pools:read');
  const canWritePool = hasPermission('pools:write');
  const canDeletePool = hasPermission('pools:delete');

  // Technician permissions
  const canReadTechnician = hasPermission('technicians:read');
  const canWriteTechnician = hasPermission('technicians:write');
  const canDeleteTechnician = hasPermission('technicians:delete');

  // Measurement permissions
  const canReadMeasurement = hasPermission('measurements:read');
  const canWriteMeasurement = hasPermission('measurements:write');

  // Chemical Products permissions
  const canReadChemicals = hasPermission('chemicals:read');
  const canWriteChemicals = hasPermission('chemicals:write');
  const canDeleteChemicals = hasPermission('chemicals:delete');

  // Chemical Inventory permissions
  const canReadInventory = hasPermission('inventory:read');
  const canWriteInventory = hasPermission('inventory:write');
  const canRegisterUsage = hasPermission('inventory:usage');

  return {
    // Core functions
    hasPermission,
    canAccessRoute,
    roleName,
    defaultRedirectPath,
    permissions,
    roleId,

    // View permissions
    canViewDashboard,
    canViewRegistro,
    canViewScuba3s,
    canViewHistorialAlertas,
    canViewAnalisis,
    canViewReportes,
    canViewConfiguracion,
    canViewQuimicos,
    canViewInventario,
    canViewAdmin,

    // Pool permissions
    canReadPool,
    canWritePool,
    canDeletePool,

    // Technician permissions
    canReadTechnician,
    canWriteTechnician,
    canDeleteTechnician,

    // Measurement permissions
    canReadMeasurement,
    canWriteMeasurement,

    // Chemical Products permissions
    canReadChemicals,
    canWriteChemicals,
    canDeleteChemicals,

    // Chemical Inventory permissions
    canReadInventory,
    canWriteInventory,
    canRegisterUsage,
  };
}
